<script setup lang="ts">
import { InteractionEventStatus, InteractionEventType } from "@/enums/mission";
import { useInteractiveReview } from "@/stores/interactive-review";
import { useMate } from "@/stores/mate";
import { InteractionEvent, Mate, MateInvitation } from "@/types/entity";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  event: InteractionEvent;
  isNeedAction: boolean;
}>();

const reviewStore = useInteractiveReview();
const mateStore = useMate();
const { t } = useI18n();

const entity = computed<MateInvitation>(() => {
  return props.event.entity! as MateInvitation;
});

const desc = computed<string>(() => {
  if (
    props.event.type === InteractionEventType.ReceivedLetterFriendInvitation
  ) {
    return t("interactive.reviewPage.invitation.receivedInvitation");
  } else if (
    props.event.type === InteractionEventType.FriendInvitationApproved
  ) {
    return t("interactive.reviewPage.invitation.invitationAccepted");
  } else if (
    props.event.type === InteractionEventType.FriendInvitationRejected
  ) {
    return t("interactive.reviewPage.invitation.invitationRejected");
  } else {
    return t("interactive.reviewPage.invitation.requestAddMate");
  }
});

const rejectText = computed<string>(() => {
  if (mateStore.isMyMateById(entity.value.senderId)) {
    return t("interactive.reviewPage.invitation.userRejected", {
      name: props.event.mate!.name,
      targetName: entity.value!.recipient?.name,
    });
  }
  return t("interactive.reviewPage.invitation.userRejectedInvitation", {
    targetName: entity.value!.sender?.name,
    name: entity.value!.recipient?.name,
  });
});

const approveText = computed<string>(() => {
  if (mateStore.isMyMateById(entity.value.senderId)) {
    return t("interactive.reviewPage.invitation.userApproved", {
      name: props.event.mate!.name,
      targetName: entity.value!.recipient?.name,
    });
  }
  return t("interactive.reviewPage.invitation.userApprovedInvitation", {
    targetName: entity.value!.sender?.name,
    name: entity.value!.recipient?.name,
  });
});

const myMate = computed<Mate | undefined>(() => {
  const { sender, recipient } = entity.value;
  if (sender) {
    if (mateStore.isMyMateById(sender.id)) {
      return sender;
    }
  }
  if (recipient) {
    if (mateStore.isMyMateById(recipient.id)) {
      return recipient;
    }
  }

  return undefined;
});

const targetMate = computed<Mate | undefined>(() => {
  const { sender, recipient } = entity.value;
  if (sender) {
    if (mateStore.isMyMateById(sender.id)) {
      return recipient;
    } else {
      return sender;
    }
  }
  if (recipient) {
    if (mateStore.isMyMateById(recipient.id)) {
      return sender;
    } else {
      return recipient;
    }
  }
  return undefined;
});
</script>
<template>
  <InteractionItemHeader
    :mate="event.mate"
    :title="event.title"
    :desc="desc"
    icon="iconify mingcute--user-add-2-fill"
    :dtCreate="event.dtCreate"
    :is-need-action="isNeedAction"
  />
  <div
    :class="{
      'text-primary-content': isNeedAction,
      'text-base-content': !isNeedAction,
    }"
  >
    {{ event.content }}
  </div>
  <RouterLink
    :to="{
      name: 'relationshipOther',
      query: {
        sourceMateId: myMate?.id,
        targeteMateId: targetMate?.id,
      },
    }"
    class="flex items-center gap-2 rounded-lg border p-2 w-fit hover:bg-base-100 hover:text-primary"
  >
    <UserAvatar :mate="targetMate" class="h-12" />
    <div>
      {{ targetMate?.name }}
    </div>
  </RouterLink>
  <div
    v-if="event.reviewStatus === InteractionEventStatus.Pending"
    class="flex flex-col gap-2"
  >
    <div class="flex gap-2">
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          reviewStore.reviewEvent(event.id, InteractionEventStatus.Approved)
        "
      >
        {{ t("Buttons.agree") }}
      </button>
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          reviewStore.reviewEvent(event.id, InteractionEventStatus.Rejected)
        "
      >
        {{ t("Buttons.deny") }}
      </button>
    </div>
  </div>
  <div v-else>
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Rejected"
      class="text-error"
    >
      {{ rejectText }}
    </div>
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Approved"
      class="text-success"
    >
      {{ approveText }}
    </div>
  </div>
</template>
